import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators
} from '@angular/forms';
import {
  Component,
  OnInit
} from '@angular/core';
import { Router } from '@angular/router';

interface CategoryPayload {
  name: string;
  featured: boolean;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  categoryImage: File;
  ogImage: File | null;
}


@Component({
  selector: 'app-add-categories',
  standalone: false,
  templateUrl: './add-categories.html',
  styleUrl: './add-categories.scss',
})
export class AddCategories implements OnInit{
  pageTitle = 'Add New Category';

  pageSubtitle =
    'Create and organize a new course category.';

  categoryForm!: FormGroup;

  isSubmitted = false;
  isSaving = false;

  categoryImage: File | null = null;
  ogImage: File | null = null;

  categoryImagePreview = '';
  ogImagePreview = '';

  categoryImageError = '';
  ogImageError = '';

  private saveAnotherAfterSubmit = false;

  private readonly categoryImageTypes: string[] = [
    'image/png'
  ];

  private readonly ogImageTypes: string[] = [
    'image/jpeg',
    'image/png'
  ];

  private readonly maximumCategoryImageSize =
    1024 * 1024;

  private readonly maximumOgImageSize =
    2 * 1024 * 1024;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  get nameControl(): AbstractControl {
    return this.categoryForm.get(
      'name'
    ) as AbstractControl;
  }

  get featuredControl(): AbstractControl {
    return this.categoryForm.get(
      'featured'
    ) as AbstractControl;
  }

  get metaTitleControl(): AbstractControl {
    return this.categoryForm.get(
      'metaTitle'
    ) as AbstractControl;
  }

  get metaDescriptionControl(): AbstractControl {
    return this.categoryForm.get(
      'metaDescription'
    ) as AbstractControl;
  }

  get metaKeywordsControl(): AbstractControl {
    return this.categoryForm.get(
      'metaKeywords'
    ) as AbstractControl;
  }

  private initializeForm(): void {
    this.categoryForm =
      this.formBuilder.group({
        name: [
          '',
          [
            Validators.required,
            Validators.maxLength(100)
          ]
        ],
        featured: [
          true,
          [
            Validators.required
          ]
        ],
        metaTitle: [
          '',
          [
            Validators.maxLength(100)
          ]
        ],
        metaDescription: [
          '',
          [
            Validators.maxLength(300)
          ]
        ],
        metaKeywords: [
          '',
          [
            Validators.maxLength(250)
          ]
        ]
      });
  }

  onCategoryImageSelected(
    event: Event
  ): void {
    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    this.categoryImageError = '';

    if (!file) {
      return;
    }

    if (
      !this.categoryImageTypes.includes(
        file.type
      )
    ) {
      this.clearCategoryImage(input);

      this.categoryImageError =
        'Only PNG images are allowed.';

      return;
    }

    if (
      file.size >
      this.maximumCategoryImageSize
    ) {
      this.clearCategoryImage(input);

      this.categoryImageError =
        'The category image must not exceed 1 MB.';

      return;
    }

    this.readImage(
      file,
      (preview: string) => {
        this.categoryImage = file;
        this.categoryImagePreview = preview;
      },
      () => {
        this.clearCategoryImage(input);

        this.categoryImageError =
          'The category image could not be read.';
      }
    );
  }

  onOgImageSelected(
    event: Event
  ): void {
    const input =
      event.target as HTMLInputElement;

    const file =
      input.files?.[0];

    this.ogImageError = '';

    if (!file) {
      return;
    }

    if (
      !this.ogImageTypes.includes(
        file.type
      )
    ) {
      this.clearOgImage(input);

      this.ogImageError =
        'Only JPEG, JPG and PNG images are allowed.';

      return;
    }

    if (
      file.size >
      this.maximumOgImageSize
    ) {
      this.clearOgImage(input);

      this.ogImageError =
        'The OG image must not exceed 2 MB.';

      return;
    }

    this.readImage(
      file,
      (preview: string) => {
        this.ogImage = file;
        this.ogImagePreview = preview;
      },
      () => {
        this.clearOgImage(input);

        this.ogImageError =
          'The OG image could not be read.';
      }
    );
  }

  submitCategory(): void {
    this.saveCategory(false);
  }

  saveAndAddAnother(): void {
    this.saveCategory(true);
  }

  cancel(): void {
    window.history.back();
  }

  private saveCategory(
    saveAnother: boolean
  ): void {
    this.isSubmitted = true;
    this.saveAnotherAfterSubmit =
      saveAnother;

    if (
      this.categoryForm.invalid ||
      !this.categoryImage ||
      this.categoryImageError ||
      this.ogImageError ||
      this.isSaving
    ) {
      this.categoryForm.markAllAsTouched();

      if (!this.categoryImage) {
        this.categoryImageError =
          'Category image is required.';
      }

      return;
    }

    const formValue =
      this.categoryForm.getRawValue();

    const payload: CategoryPayload = {
      name: formValue.name.trim(),
      featured: formValue.featured,
      metaTitle:
        formValue.metaTitle.trim(),
      metaDescription:
        formValue.metaDescription.trim(),
      metaKeywords:
        formValue.metaKeywords.trim(),
      categoryImage:
        this.categoryImage,
      ogImage:
        this.ogImage
    };

    const formData = new FormData();

    formData.append(
      'name',
      payload.name
    );

    formData.append(
      'featured',
      String(payload.featured)
    );

    formData.append(
      'metaTitle',
      payload.metaTitle
    );

    formData.append(
      'metaDescription',
      payload.metaDescription
    );

    formData.append(
      'metaKeywords',
      payload.metaKeywords
    );

    formData.append(
      'categoryImage',
      payload.categoryImage
    );

    if (payload.ogImage) {
      formData.append(
        'ogImage',
        payload.ogImage
      );
    }

    this.isSaving = true;

    console.log(
      'Category payload:',
      payload
    );

    console.log(
      'Category form data:',
      formData
    );

    /*
    Replace the timeout with your API:

    this.categoryService
      .createCategory(formData)
      .pipe(
        finalize(() => {
          this.isSaving = false;
        })
      )
      .subscribe({
        next: () => {
          this.handleSaveSuccess();
        },
        error: (error) => {
          console.error(
            'Category creation failed:',
            error
          );
        }
      });
    */

    setTimeout(() => {
      this.isSaving = false;
      this.handleSaveSuccess();
    }, 800);
  }

  private handleSaveSuccess(): void {
    this.isSubmitted = false;

    if (this.saveAnotherAfterSubmit) {
      this.resetForm();
      return;
    }

    this.router.navigate([
      '/admin/course-reference/category'
    ]);
  }

  private resetForm(): void {
    this.categoryForm.reset({
      name: '',
      featured: true,
      metaTitle: '',
      metaDescription: '',
      metaKeywords: ''
    });

    this.categoryImage = null;
    this.ogImage = null;

    this.categoryImagePreview = '';
    this.ogImagePreview = '';

    this.categoryImageError = '';
    this.ogImageError = '';

    this.saveAnotherAfterSubmit = false;
  }

  private readImage(
    file: File,
    onLoad: (preview: string) => void,
    onError: () => void
  ): void {
    const reader = new FileReader();

    reader.onload = () => {
      if (
        typeof reader.result === 'string'
      ) {
        onLoad(reader.result);
      }
    };

    reader.onerror = () => {
      onError();
    };

    reader.readAsDataURL(file);
  }

  private clearCategoryImage(
    input: HTMLInputElement
  ): void {
    input.value = '';
    this.categoryImage = null;
    this.categoryImagePreview = '';
  }

  private clearOgImage(
    input: HTMLInputElement
  ): void {
    input.value = '';
    this.ogImage = null;
    this.ogImagePreview = '';
  }
  setFeatured(featured: boolean): void {
  this.featuredControl.setValue(featured);
  this.featuredControl.markAsDirty();
  this.featuredControl.markAsTouched();
}
}
